import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import type { Article, Category } from '@/types/content';

const list = articles as Article[];
const cats = categories as Category[];
const PAGE_SIZE = 6;

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get('cat') || 'all';
  const query = searchParams.get('q') || '';
  const [sort, setSort] = useState<'new' | 'hot'>('new');
  const [searchInput, setSearchInput] = useState(query);
  const [visible, setVisible] = useState(PAGE_SIZE);

  const currentCat = cats.find((c) => c.id === activeCat);

  useEffect(() => {
    setSearchInput(query);
  }, [query]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [activeCat, query, sort]);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    let result = list.filter((a) => (activeCat === 'all' ? true : a.category === activeCat));
    if (keyword) {
      result = result.filter((a) =>
        [a.title, a.excerpt, a.categoryLabel, a.author, a.tags.join(' ')]
          .join(' ')
          .toLowerCase()
          .includes(keyword),
      );
    }
    return [...result].sort((a, b) =>
      sort === 'hot' ? b.heat - a.heat : new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [activeCat, query, sort]);

  const shown = filtered.slice(0, visible);

  const schema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '瓜料库 - 福利吃瓜',
      description: '按档期筛选福利吃瓜的全部内容，支持关键词搜索与热度排序。',
      url: 'https://coulex.cn/topics',
      hasPart: filtered.slice(0, 10).map((a) => ({
        '@type': 'Article',
        headline: a.title,
        url: `https://coulex.cn/article/${a.id}`,
        datePublished: a.date,
      })),
    }),
    [filtered],
  );

  usePageMeta({
    title: currentCat
      ? `${currentCat.name} - 福利吃瓜瓜料库`
      : '瓜料库 - 福利吃瓜全部内容与热点合集',
    description: currentCat
      ? `福利吃瓜「${currentCat.name}」栏目合集：${currentCat.desc}`
      : '福利吃瓜瓜料库汇总今日上新、本周推荐、周末限定、假日特辑与近期热点全部内容，支持按档期筛选、关键词搜索与热度排序。',
    keywords: '福利吃瓜,瓜料库,福利吃瓜今日上新,福利吃瓜本周推荐,福利吃瓜近期热点',
    path: currentCat ? `/topics?cat=${currentCat.id}` : '/topics',
    schema,
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== 'all') next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-16 md:pt-20">
        <section className="bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
            <Breadcrumbs
              items={[
                { label: '瓜料库', to: currentCat ? '/topics' : undefined },
                ...(currentCat ? [{ label: currentCat.name }] : []),
              ]}
            />
            <div className="mt-5 flex flex-col lg:flex-row lg:items-end justify-between gap-5">
              <div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
                  {currentCat ? `${currentCat.name}` : '瓜料库 · 全部内容'}
                </h1>
                <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-7">
                  {currentCat
                    ? currentCat.desc
                    : '按档期浏览福利吃瓜的全部瓜料。你可以用关键词搜索，也可以切换到最热排序，看看大家正在讨论什么。'}
                </p>
              </div>
              <p className="text-sm text-foreground-500 shrink-0">
                共 <span className="font-heading font-bold text-primary-600">{filtered.length}</span> 篇内容
              </p>
            </div>
          </div>
        </section>

        <section className="sticky top-16 md:top-20 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200">
          <div className="w-full px-4 md:px-8 lg:px-12 py-3">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
                <button
                  type="button"
                  onClick={() => updateParam('cat', 'all')}
                  className={`px-3.5 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                    activeCat === 'all'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  全部
                </button>
                {cats.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => updateParam('cat', c.id)}
                    className={`px-3.5 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                      activeCat === c.id
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 lg:ml-auto">
                <form
                  className="relative flex-1 lg:w-64"
                  onSubmit={(e) => {
                    e.preventDefault();
                    updateParam('q', searchInput.trim());
                  }}
                >
                  <i
                    className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"
                    aria-hidden="true"
                  ></i>
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="搜索瓜料关键词"
                    aria-label="搜索瓜料"
                    className="w-full pl-9 pr-3 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </form>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as 'new' | 'hot')}
                  aria-label="排序方式"
                  className="px-3 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300"
                >
                  <option value="new">最新发布</option>
                  <option value="hot">按热度排序</option>
                </select>
                {query && (
                  <button
                    type="button"
                    onClick={() => updateParam('q', '')}
                    aria-label="清除搜索"
                    className="w-10 h-10 shrink-0 rounded-md bg-background-100 border border-background-200 text-foreground-600 hover:bg-background-200 flex items-center justify-center transition-colors"
                  >
                    <i className="ri-close-line text-lg" aria-hidden="true"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="w-full px-4 md:px-8 lg:px-12">
            {query && (
              <p className="mb-6 text-sm text-foreground-600">
                关键词「<span className="text-foreground-950 font-medium">{query}</span>」共找到{' '}
                <span className="text-primary-600 font-medium">{filtered.length}</span> 条结果
              </p>
            )}

            {shown.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {shown.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
                {visible < filtered.length && (
                  <div className="mt-10 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-foreground-950 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
                    >
                      <i className="ri-arrow-down-line" aria-hidden="true"></i>
                      加载更多（剩余 {filtered.length - visible} 篇）
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="py-16 text-center rounded-lg border border-dashed border-background-300">
                <span className="inline-flex w-14 h-14 rounded-full bg-background-100 text-foreground-400 items-center justify-center">
                  <i className="ri-emotion-sad-line text-2xl" aria-hidden="true"></i>
                </span>
                <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">没有找到相关的瓜料</h2>
                <p className="mt-2 text-sm text-foreground-600">换个关键词试试，或者直接浏览全部档期内容。</p>
                <button
                  type="button"
                  onClick={() => setSearchParams({})}
                  className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100 border-t border-background-200">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              换个档期继续看
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {cats
                .filter((c) => c.id !== activeCat)
                .slice(0, 3)
                .map((c) => (
                  <Link
                    key={c.id}
                    to={`/topics?cat=${c.id}`}
                    className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5 hover:border-primary-300 transition-colors"
                  >
                    <span className="w-11 h-11 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                      <i className={`${c.icon} text-xl`} aria-hidden="true"></i>
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-heading font-bold text-foreground-950 group-hover:text-primary-600 transition-colors">
                        {c.name}
                      </span>
                      <span className="block text-xs text-foreground-500 mt-1 line-clamp-1">{c.desc}</span>
                    </span>
                    <i className="ri-arrow-right-line text-foreground-400" aria-hidden="true"></i>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}